import { PURCHASE_TYPE } from './constants';
import { PRO_REGISTRATION_PERSONAS } from '../../../views/auth/RegisterView/helpers';
import { BASENAME } from '../../../constants/basenames';
import { SEARCH_SUBSCRIPTION_PLAN } from '../../../apps/temp/views/onboarding/PhotoSearchCheckout/helpers';

/**
 * Given the purchase type and metadata, retrieve the query params to pass to the thank you modal / page.
 *
 * @param {String} purchaseType the purchase type
 * @param {Object} purchaseMetadata the purchase metadata
 *
 * @returns {{product_type: string, vertical_type: string}}
 */
export const getPurchaseMetadataParams = (purchaseType, purchaseMetadata) => {
  const params = {
    product_type: '',
    vertical_type: '',
  };

  const {
    eventAddonDetails,
    subscriptionDetails,
    verticalDetails,
  } = purchaseMetadata;

  switch (purchaseType) {
    case PURCHASE_TYPE.EVENTS: {
      params.product_type = eventAddonDetails.eventAddonItemTypes.join('|');
      params.vertical_type = 'consumer';
      break;
    }
    case PURCHASE_TYPE.PHOTO_MANAGER_SUBSCRIPTION: {
      params.product_type = `PM${
        subscriptionDetails.freeTrialAvailable ? '_TRIAL' : '_PAID'
      }`;
      params.vertical_type =
        verticalDetails?.persona?.value ??
        (verticalDetails?.basename ?? '')?.toLowerCase();
      break;
    }
    case PURCHASE_TYPE.SEARCH_SUBSCRIPTION: {
      const { searchSubscriptionItem } = subscriptionDetails;
      params.product_type = `SB_${searchSubscriptionItem.plan}${
        subscriptionDetails.freeTrialAvailable ? '_TRIAL' : '_PAID'
      }`;
      params.vertical_type = 'consumer';
      break;
    }
    default: {
      break;
    }
  }

  return params;
};

/**
 * Given a set of parameters in the shape of those generated by the purchaseMetadata, reverse engineer them
 *
 * @param {Object} params the params
 *
 * @returns {{purchaseMetadata: { eventAddonDetails: object, subscriptionDetails: object, verticalDetails: object }, purchaseType: string}}
 */
export const getPurchaseMetadataFromParams = params => {
  const { product_type: productType, vertical_type: verticalType } = params;
  let purchaseType = PURCHASE_TYPE.EVENTS;
  const purchaseMetadata = {
    eventAddonDetails: undefined,
    subscriptionDetails: undefined,
    verticalDetails: undefined,
  };

  if (!productType || !verticalType) {
    return { purchaseType, purchaseMetadata };
  }

  if (productType.includes('SB_')) {
    purchaseType = PURCHASE_TYPE.SEARCH_SUBSCRIPTION;
  }

  if (productType.includes('PM_')) {
    purchaseType = PURCHASE_TYPE.PHOTO_MANAGER_SUBSCRIPTION;
  }

  //  Prepare verticalDetails
  const persona = PRO_REGISTRATION_PERSONAS.find(p => p.value === verticalType);
  const basename = BASENAME[verticalType];
  if (persona || basename) {
    purchaseMetadata.verticalDetails = {
      persona,
      basename,
    };
  }

  // Prepare eventAddonDetails
  if (purchaseType === PURCHASE_TYPE.EVENTS) {
    purchaseMetadata.eventAddonDetails = {
      eventAddonItemTypes: productType.split('|'),
      freeEventConfigured: productType.includes('FREE'),
    };
  }

  // prepare photoManagerSubscription
  if (purchaseType === PURCHASE_TYPE.PHOTO_MANAGER_SUBSCRIPTION) {
    purchaseMetadata.subscriptionDetails = {
      // Contents themselves are irrelevant, it's existence is enough.
      photoManagerSubscriptionItem: {},
      freeTrialAvailable: productType.includes('TRIAL'),
    };
  }

  // prepare searchSubscription
  if (purchaseType === PURCHASE_TYPE.SEARCH_SUBSCRIPTION) {
    purchaseMetadata.subscriptionDetails = {
      // Contents themselves are irrelevant other than plan.
      searchSubscriptionItem: {
        plan: productType.includes(SEARCH_SUBSCRIPTION_PLAN.SHUTTERBUG_PLUS)
          ? SEARCH_SUBSCRIPTION_PLAN.SHUTTERBUG_PLUS
          : SEARCH_SUBSCRIPTION_PLAN.SHUTTERBUG_PREMIUM,
      },
      freeTrialAvailable: productType.includes('TRIAL'),
    };
  }

  return {
    purchaseType,
    purchaseMetadata,
  };
};
